import { useState, useEffect } from "react";
import { Plus, ArrowLeft } from "lucide-react";
import { getSOSAsuntos, SOSAsunto } from "../../../api/sosService";
import { useAuth } from "../../../context/AuthContext";
import { CrearAsuntoModal } from "./CrearAsuntoModal";
import { EditarAsuntoModal } from "./EditarAsuntoModal";

interface SOSAsuntosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SOSAsuntosModal({ isOpen, onClose }: SOSAsuntosModalProps) {
  const { session } = useAuth();
  const [asuntos, setAsuntos] = useState<SOSAsunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCrearModalOpen, setIsCrearModalOpen] = useState(false);
  const [asuntoEditando, setAsuntoEditando] = useState<SOSAsunto | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchAsuntos();
    }
  }, [isOpen]);

  const fetchAsuntos = async () => {
    if (!session?.unidad?.id) return;
    setLoading(true);
    try {
      const data = await getSOSAsuntos(session.unidad.id);
      setAsuntos(data);
    } catch (error) {
      console.error("Error al cargar asuntos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatedOrModified = () => {
    setIsCrearModalOpen(false);
    setAsuntoEditando(null);
    fetchAsuntos();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition"
                title="Volver"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-lg font-semibold text-gray-800">
                Asuntos SOS
              </h2>
            </div>
            <button
              onClick={() => setIsCrearModalOpen(true)}
              className="flex items-center gap-1 bg-red-500 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-red-600 transition shadow-sm"
              title="Crear Nuevo Asunto"
            >
              <Plus size={16} /> Crear
            </button>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto flex-1 bg-gray-50">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
              </div>
            ) : asuntos.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                <p>No hay asuntos configurados.</p>
                <p className="text-sm mt-1">Haga clic en el botón "Crear" para añadir uno nuevo.</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nombre</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Prioridad</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Activo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {asuntos.map((asunto) => (
                      <tr
                        key={asunto.sas_codigo}
                        className="hover:bg-red-50 cursor-pointer transition"
                        onClick={() => setAsuntoEditando(asunto)}
                        title="Clic para editar"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{asunto.sas_codigo}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{asunto.sas_nombre}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500">{asunto.sas_prioridad}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            asunto.sas_activo === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {asunto.sas_activo_nombre}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && asuntos.length > 0 && (
              <p className="text-xs text-gray-500 mt-2 text-center">Haz clic sobre un registro para editarlo.</p>
            )}
          </div>
        </div>
      </div>

      <CrearAsuntoModal
        isOpen={isCrearModalOpen}
        onClose={() => setIsCrearModalOpen(false)}
        onCreated={handleCreatedOrModified}
      />

      {asuntoEditando && (
        <EditarAsuntoModal
          isOpen={true}
          asunto={asuntoEditando}
          onClose={() => setAsuntoEditando(null)}
          onModified={handleCreatedOrModified}
        />
      )}
    </>
  );
}
